import React, { useEffect, useRef, useState } from 'react'
import Select from 'components/select'
import Field from 'components/field'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { getProjectTables } from 'helpers/utils'
import api from 'helpers/api'
import { ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS, BLOCK_SELECT_OPTION_TYPES } from 'components/spreadsheet/constants/const'
import { RESTRICTED_SOURCE_PLACEHOLDER } from 'app-constants'
import { ITable, ITableViewWithColumns } from 'types'

export const LIVE_VALUES_HELP =
  'Non-blank values are automatically updated from the source table. Make sure the source values are unique ' +
  'to avoid unintended updates. Emptying a value in the source table will result in a validation warning, ' +
  'wherever that value is selected.'

export const AUTOPOPULATE_HELP =
  'If your select list only has one value, new rows will automatically populate with this value. If there ' +
  'is more than one then no autopopulate will take place.'

interface SourceOption {
  label: string
  value: string
}

// How a picker labels its selected id. Normally that's the matching option, but a source configured by
// another admin can be missing from the options: the table and view lists only hold what the user can open
// at the table level, while the source view itself may still be readable through a view-level role (the
// same rule the backend applies to lookup sources). Then the name the view fetch returned is shown instead.
// Only when neither is available is the source restricted, and it shows the redacted placeholder (the same
// one lookups use) rather than a blank select. Neither label is offered as a pickable option.
const getSelection = (
  selectedId: string,
  options: SourceOption[] | undefined,
  placeholder: string,
  sourceLabel?: string
): { optionsSelected: string[]; placeholder: string; restricted: boolean } => {
  if (!selectedId || !options || options.some((option) => option.value === selectedId)) {
    return { optionsSelected: selectedId ? [selectedId] : [], placeholder, restricted: false }
  }
  if (sourceLabel) return { optionsSelected: [], placeholder: sourceLabel, restricted: false }
  return { optionsSelected: [], placeholder: RESTRICTED_SOURCE_PLACEHOLDER, restricted: true }
}

interface SelectSourceFieldsProps {
  // Current selection (controlled by the consumer).
  tableId: string
  viewId: string
  columnId: string
  onTableChange: (tableId: string) => void
  onViewChange: (viewId: string) => void
  onColumnChange: (columnId: string) => void
  disabled?: boolean
  // The selected source view as the user can read it (with its columns and table), once it has loaded.
  onViewLoaded?: (view: ITableViewWithColumns) => void
  // Called with true while any of the three pickers shows the redacted placeholder, false otherwise.
  onRestrictedChange?: (restricted: boolean) => void
}

// The shared "source table → view → column" dependent dropdowns for a column that sources its options
// from another table. Owns the tables/views/columns fetches; the selection itself is controlled by the
// consumer. Views are limited to Spreadsheet views and column options exclude kinds that can't be a
// select source (BLOCK_SELECT_OPTION_TYPES).
const SelectSourceFields: React.FC<SelectSourceFieldsProps> = ({
  tableId,
  viewId,
  columnId,
  onTableChange,
  onViewChange,
  onColumnChange,
  disabled = false,
  onViewLoaded,
  onRestrictedChange
}) => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()

  const [tables, setTables] = useState<ITable[] | undefined>(undefined)
  const [views, setViews] = useState<ITableViewWithColumns[] | undefined>(undefined)
  // The last view fetched (null when the user can't read it), keyed by its id so a result is never read
  // against a different selection while the next fetch is in flight.
  const [fetchedView, setFetchedView] = useState<{ viewId: string; view: ITableViewWithColumns | null }>()

  // The store's error reporter and the consumer's callbacks kept in a ref (refreshed in an effect, never
  // written during render — a React Compiler rule) so the id-keyed fetch effects don't list them as deps
  // and re-run whenever an inline callback's identity changes.
  const handlersRef = useRef({ displayErrorMessage, onViewLoaded, onRestrictedChange })
  useEffect(() => {
    handlersRef.current = { displayErrorMessage, onViewLoaded, onRestrictedChange }
  })

  // Until the source view has loaded it isn't known whether a table/view missing from its list is readable
  // through that view, so neither is judged restricted yet.
  const sourceView = viewId && fetchedView?.viewId === viewId ? fetchedView.view : undefined
  const viewPending = Boolean(viewId) && sourceView === undefined
  const columns = sourceView === null ? [] : sourceView?.columns
  const tableOptions = tables?.map((table) => ({ label: table.name, value: table.publicId }))
  const viewOptions = views?.map((view) => ({ label: view.name, value: view.publicId }))
  const columnOptions = columns
    ?.filter((column) => !BLOCK_SELECT_OPTION_TYPES.includes(column.kind))
    .map((column) => ({ label: column.name, value: column.publicId }))

  const { restricted: tableRestricted, ...tableSelection } = getSelection(
    tableId,
    viewPending ? undefined : tableOptions,
    'Select a table',
    sourceView?.documentTable?.publicId === tableId ? sourceView.documentTable.name : undefined
  )
  const { restricted: viewRestricted, ...viewSelection } = getSelection(
    viewId,
    viewPending ? undefined : viewOptions,
    'Select a view',
    sourceView?.name
  )
  const { restricted: columnRestricted, ...columnSelection } = getSelection(columnId, columnOptions, 'Select a column')

  // Reported so the consumer can explain the missing access and hide what depends on the source (its
  // dependency columns and sourced values), which has nothing meaningful to show once it's hidden.
  const restricted = tableRestricted || viewRestricted || columnRestricted
  useEffect(() => {
    handlersRef.current.onRestrictedChange?.(restricted)
  }, [restricted])

  // Tables for the project.
  useEffect(() => {
    getProjectTables(project.publicId, false)
      .then(setTables)
      .catch((error) => handlersRef.current.displayErrorMessage(error))
  }, [project.publicId])

  // Views for the selected table (Spreadsheet views only), refetched whenever the table changes.
  useEffect(() => {
    setViews(undefined)
    if (!tableId) return
    api
      .getTableViews(tableId, { ignoreColumns: true })
      .then((response) => {
        const allowed = response.data
          .filter((view) => ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS.includes(view.type))
          .sort((viewA, viewB) => viewA.name.localeCompare(viewB.name))
        setViews(allowed)
      })
      .catch((error) => {
        setViews([])
        // A source configured by another admin can point at a table this user can't open. That isn't an
        // error to report: the pickers show what the user can't see as restricted.
        if (error?.status !== 403) handlersRef.current.displayErrorMessage(error)
      })
  }, [tableId])

  // The selected view, refetched whenever the view changes: its columns feed the column picker. The view's
  // own cached select options are not needed here (only the column list is), so they're left out.
  useEffect(() => {
    if (!viewId) return
    api
      .getTableView(viewId, { ignoreCachedOptions: true })
      .then((response) => {
        setFetchedView({ viewId, view: response.data })
        handlersRef.current.onViewLoaded?.(response.data)
      })
      .catch((error) => {
        setFetchedView({ viewId, view: null })
        if (error?.status !== 403) handlersRef.current.displayErrorMessage(error)
      })
  }, [viewId])

  return (
    <>
      <Field label="Source Table" htmlFor="source-table-select" className="px-2.5 pt-0">
        <Select
          inputId="source-table-select"
          options={tableOptions ?? []}
          {...tableSelection}
          onOptionClick={onTableChange}
          loading={tables === undefined}
          disabled={disabled}
        />
      </Field>

      <Field label="Source View" htmlFor="source-view-select">
        <Select
          inputId="source-view-select"
          options={viewOptions ?? []}
          {...viewSelection}
          onOptionClick={onViewChange}
          disabled={disabled || !tableId}
          loading={Boolean(tableId) && views === undefined}
        />
      </Field>

      <Field label="Source Column" htmlFor="source-column-select">
        <Select
          inputId="source-column-select"
          options={columnOptions ?? []}
          {...columnSelection}
          onOptionClick={onColumnChange}
          disabled={disabled || !viewId}
          loading={viewPending}
        />
      </Field>
    </>
  )
}

export default SelectSourceFields
